/**
 * Record-body layout constants — the geometry contract between
 * `RecordBody` (the scroll region's content) and sections
 * that deliberately break out of it.
 *
 * These pairs are COUPLED: a bleed only makes sense relative to the gutter
 * it cancels. Defining both halves side by side is what keeps a future
 * gutter change from silently misaligning every full-width section — change
 * one line here and its counterpart is staring at you.
 */

/** Horizontal gutter of the record body column (28px each side). */
export const RECORD_BODY_GUTTER = 'px-7';

/**
 * Cancels RECORD_BODY_GUTTER exactly — for elements that must span the full
 * column width (e.g. the tabbed layout's full-width divider).
 */
export const RECORD_BODY_FULL_BLEED = '-mx-7';

/**
 * Partial bleed for reading surfaces (e.g. the email-thread conversation):
 * cancels most of RECORD_BODY_GUTTER, leaving a 16px gutter so the
 * conversation aligns with the compact header/reply footer (both px-4 = 16px)
 * while a bordered surface never touches the column edge.
 */
export const RECORD_BODY_BLEED = '-mx-3';

/**
 * Classes for the `SlideoutMenu.Header` that hosts a record header. The type
 * chip row is 36px (`RECORD_HEADER_ROW_CLASS`) — the height of the actions
 * cluster's buttons and the chrome's close — and shares the header's FIRST
 * row with that cluster: with `RECORD_HEADER_ALIGN` (`start`), the chip row
 * sits flush against the actions/close cluster on one line. The identity
 * block (avatar, title, sub-header) renders full-width in the header's
 * SECOND row, via `headerSecondRow` (`SlideoutMenu`) / `secondRow`
 * (`SlideoutMenu.Header`) — it no longer shares a row with the actions
 * cluster. The divider is an inset pseudo-element so it still spans the
 * full width of the header, beneath both rows. Zeroes the chrome's `md:`
 * padding variant explicitly — tailwind-merge treats it as a separate group
 * from the bare utility.
 */
export const RECORD_HEADER_CLASS =
  'px-4 pt-3 pb-3 md:px-4 ' +
  'after:pointer-events-none after:absolute after:inset-x-[17px] after:bottom-0 ' +
  'after:border-t after:border-[var(--color-border-secondary)] after:content-[""]';
export const RECORD_HEADER_ALIGN = 'start' as const;
/** The type chip row — matches the height of the header's action buttons. */
export const RECORD_HEADER_ROW_CLASS = 'flex min-h-9 items-center gap-2';
